import { useCallback } from 'react';
import { type DefaultError, useMutation } from '@tanstack/react-query';

import { logger } from '@zep/core-logger';

import { AI_HTTP_CLIENT } from '@/api/ai/client';
import { useLocale } from '@/hooks';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowAiErrorAlert } from '@/views/build/hook';

import { useQuizFormField } from '../../hook/useQuizFormField';
import { useQuizControl } from '../../QuizControlProvider';

import { type AiGenerateSingleSubjectiveAnswerQuery } from './type';
import { useSetAiGenerateSubjectiveStatus } from './useAiGeneateSubjectiveStatus';

export const useAiGenerateSingleAnswer = () => {
  const { mutateAsync, isPending } =
    useAiGenerateSingleSubjectiveAnswerMutation();

  const { addIndex, removeIndex } = useSetAiGenerateSubjectiveStatus();

  const [answer] = useQuizFormField({
    field: 'subjectiveData.answerList.0.text',
  });
  const [question] = useQuizFormField({
    field: 'question',
  });

  const { id } = useQuizControl();
  const { setValue, getValues } = useBuildFormContext();
  const disabled = isPending || !question;

  const showAiErrorAlert = useShowAiErrorAlert();

  const handleGeneratedAnswer = useCallback(
    (generatedAnswer: string) => {
      const quizList = getValues(`quizList`);
      const targetQuizIndex = quizList.findIndex(quiz => quiz.id === id);
      if (targetQuizIndex === -1) {
        console.error(`target quiz not found: ${id}`);
        return;
      }

      setValue(
        `quizList.${targetQuizIndex}.subjectiveData.answerList.0.text`,
        generatedAnswer,
      );
    },
    [setValue, id, getValues],
  );

  const { locale } = useLocale();

  const generate = useCallback(async () => {
    const start = Date.now();
    logger.track('add_subjective_answer_ai_start', {
      location: 'single',
    });
    addIndex(0);
    try {
      const generatedAnswer = await mutateAsync({
        question,
        previousOutput: answer,
        locale,
      });
      generatedAnswer && handleGeneratedAnswer(generatedAnswer);
      const duration = (Date.now() - start) / 1000;
      logger.track('add_subjective_answer_ai_end', {
        location: 'single',
        duration_seconds: duration,
      });
    } catch (_) {
      const duration = (Date.now() - start) / 1000;
      logger.track('add_subjective_answer_ai_error', {
        location: 'single',
        duration_seconds: duration,
      });
      showAiErrorAlert();
    } finally {
      removeIndex(0);
    }
  }, [
    mutateAsync,
    question,
    answer,
    handleGeneratedAnswer,
    locale,
    showAiErrorAlert,
    addIndex,
    removeIndex,
  ]);

  return {
    disabled,
    generate,
  };
};

export const useAiGenerateSingleSubjectiveAnswerMutation = () => {
  return useMutation<
    string,
    DefaultError,
    AiGenerateSingleSubjectiveAnswerQuery
  >({
    mutationFn: async query => {
      const res = await AI_HTTP_CLIENT.post(
        `/quiz-builder/generate/single-subjective-answer`,
        query,
      );
      return res.data;
    },
  });
};
